import React, { useState, useEffect } from 'react';
import { JobfairService } from 'app/services/JobfairService';
import { JobFairBillingService } from '../../../../services/JobFairBillingService';
import { useAuth } from 'app/context/auth-context';
import { JobFair } from 'app/types';
import {
  Pencil,
  Trash,
  Plus,
  Calendar3,
  Clock,
  HourglassSplit,
  Clipboard,
  ExclamationTriangle
} from "react-bootstrap-icons";
import { Alert, Button, Modal } from 'react-bootstrap-v5';
import { AppModal } from 'app/modules/shared/modals/AppModal';
import { AlertModal } from 'app/modules/shared/modals/AlertModal';
import CreateEditJobFair from 'app/modules/shared/jobfairs/CreateEditJobFair';
import { config } from 'config';
import { OrgService } from 'app/services/OrgService';
import moment from 'moment';
import { getTimezoneOffset } from 'app/helpers/MiscUtils';

type ManageJobFairProps = {
  onLoad: (isAnyPaidJobFairLoaded: boolean) => void;
  onSponsorInvoicedCreate: () => void;
  orgOnBoardingProgressPercentage: number;
  orgName: string
}

const ManageJobFairs: React.FC<ManageJobFairProps> = (props: ManageJobFairProps) => {
  const [jobFairs, setJobFairs] = useState<JobFair[]>([]);
  const [showModal, setShowModal] = useState(false);
  const jobFairService = new JobfairService();
  const { user } = useAuth();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [jobFairToDelete, setJobFairToDelete] = useState<JobFair | null>(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedJobFair, setSelectedJobFair] = useState<JobFair | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [promoCodes, setPromoCodes] = useState<{[key: string]: string}>({});
  const [showPast, setShowPast] = useState(false);
  const [jobfairPaymentsEnabled, setJobfairPaymentsEnabled] = useState(true);
  const [billingConfigLoaded, setBillingConfigLoaded] = useState(false);

  useEffect(() => {
    if (!user?.organizationId) return;
    fetchJobFairs();
    fetchJobFairBilling();
  }, [user?.organizationId]);

  const fetchJobFairBilling = async () => {
    try {
      if (!user?.organizationId) return;
      const billingConfig = await new JobFairBillingService().getConfig(user.organizationId.toString());
      setJobfairPaymentsEnabled(billingConfig.jobfairPaymentsEnabled);
      setBillingConfigLoaded(true);
    } catch (error) {
      console.error('Failed to load job fair billing configuration', error);
    }
  };

  const fetchJobFairs = async () => {
    setIsLoading(true);
    if (!user?.organizationId) return;
    const fairs = await jobFairService.getJobFairs(user.organizationId);
    props.onLoad(fairs.some(f => f.paymentStatus === 'Paid'));
    const sortedFairs = fairs.sort((a, b) => {
      const dateTimeA = new Date(`${a.startDate}T${a.startTime}`);
      const dateTimeB = new Date(`${b.startDate}T${b.startTime}`);
      return dateTimeA.getTime() - dateTimeB.getTime();
    });
    setJobFairs(sortedFairs);
    setIsLoading(false);
  };

  const handleCreateJobFair = async (values: any) => {
    if (!user?.id) return;
    if (selectedJobFair) {
      await jobFairService.updateJobFair({ ...selectedJobFair, ...values });
      fetchJobFairs();
      setShowModal(false);
    } else {
      const jf = await jobFairService.createJobFair(user.organizationId.toString(), values);
      if (props.orgOnBoardingProgressPercentage < 40) {
        await new OrgService().updateOrg(user.organizationId.toString(), {
          onBoardingProgress: {
            progress: 40,
            lastCompletedStep: 'CreatedJobFair'
          }
        });
      }
      if (jf.paymentStatus === 'UnPaid') {
        await handlePayment(jf);
      } else {
        setShowModal(false);
        await fetchJobFairs();
        props.onSponsorInvoicedCreate();
      }
    }
  };

  const handleDeleteJobFair = async (jobFair: JobFair) => {
    setJobFairToDelete(jobFair);
    setShowDeleteAlert(true);
  };

  const handleEditJobFair = async (jobFair: JobFair) => {
    setSelectedJobFair(jobFair);
    setShowModal(true);
  }

  const confirmDeleteJobFair = async () => {
    if (jobFairToDelete && jobFairToDelete.paymentStatus === 'UnPaid') {
      await jobFairService.deleteJobFair(jobFairToDelete.companyId, jobFairToDelete.id);
      fetchJobFairs();
    }
    setShowDeleteAlert(false);
    setJobFairToDelete(null);
  };

    const handlePayment = async (jobFair: JobFair) => {
      setIsPaymentLoading(jobFair.id);
      try {
        const paymentLinkUrl = config.stripePaymentLink;
        if (paymentLinkUrl) {
          let externalPaymentUrl = `${paymentLinkUrl}?client_reference_id=${jobFair.companyId}___${jobFair.id}&prefilled_email=${encodeURIComponent(user?.email || '')}`;
          const code = promoCodes[jobFair.id];
          if (code && code.trim() !== '') {
            externalPaymentUrl += `&prefilled_promo_code=${encodeURIComponent(code.trim())}`;
          }
          window.location.href = externalPaymentUrl;
        } else {
          console.error('Stripe payment link not configured');
          setErrorMessage('Payment link configuration is missing. Please contact support.');
          setShowErrorModal(true);
        }
      } catch (error) {
        setErrorMessage('An error occurred with the payment gateway. Please try again later.');
        setShowErrorModal(true);
      }
      finally {
        setIsPaymentLoading(null);
      }
    };

  const visibleFairs = showPast
    ? jobFairs
    : jobFairs.filter(fair => {
        const tzOffset = getTimezoneOffset(fair.timezone);
        const fairDateInBrowserTz = moment(
          `${fair.startDate} ${fair.startTime} ${tzOffset}`,
          'YYYY-MM-DD HH:mm ZZ'
        ).local().format('YYYY-MM-DD');
        const todayInBrowserTz = moment().format('YYYY-MM-DD');
        return fairDateInBrowserTz >= todayInBrowserTz;
      });

  return (
    <div className='card card-flush h-xl-100'>
      <div className='card-header pt-7'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{jobFairs.length > 0 ? "Manage Job Fairs" : "Job Fair Registration"}</span>
        </h3>
        <div className='card-toolbar'>
          {jobFairs.length > 0 && (
            <>
              <div className="form-check form-switch form-check-custom form-check-solid me-4 d-flex align-items-center">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  role="switch"
                  id="showPastSwitch"
                  checked={showPast}
                  onChange={(e) => setShowPast(e.target.checked)}
                />
                <label className="form-check-label fs-7 fw-bold text-gray-600 cursor-pointer" htmlFor="showPastSwitch">
                  Show Past
                </label>
              </div>
              <button className='btn btn-sm btn-light-primary' onClick={() => { setSelectedJobFair(undefined); setShowModal(true); }}>
                <Plus className='fs-2 me-2' size={20} /> <b>Create New Job Fair</b>
              </button>
            </>
          )}
        </div>
      </div>
      <div className='card-body py-4'>
        {isLoading ? (<div className='d-flex justify-content-center py-10'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>) : (
          <div className='row g-6'>
            {visibleFairs.length > 0 ? (
              <>{
                visibleFairs.map((fair: JobFair) => (
                  <div key={fair.id} className='col-12 col-xl-6'>
                    <div className='card h-100 shadow-sm hover-elevate-up transition-300'>
                      <div className='card-header border-0 pt-9 ribbon ribbon-top ribbon-clip'>
                        {fair.paymentStatus == 'Paid' &&
                          <div className={`ribbon-label`}>
                            Paid
                            <span className="ribbon-inner bg-success"></span>
                          </div>
                        }
                        <div className='card-title m-0'>
                          <span className='fs-3 fw-bolder text-dark'>{fair.name}</span>
                        </div>
                        <div className='card-toolbar'>
                          <div className="d-flex">
                            <Button variant='light' size="sm" className="btn-icon btn-active-light-primary me-2 shadow-sm" onClick={() => handleEditJobFair(fair)} title="Edit">
                              <Pencil size={16} />
                            </Button>
                            {fair.paymentStatus == 'UnPaid' &&
                              <Button variant='light' size="sm" className="btn-icon btn-active-light-danger shadow-sm" onClick={() => handleDeleteJobFair(fair)} title="Delete">
                                <Trash size={16} />
                              </Button>}
                          </div>
                        </div>

                      </div>
                      <div className='card-body p-9'>
                        <div className='d-flex align-items-center mb-6'>
                          <HourglassSplit className='text-primary me-2' size={20} />
                          <span className="fs-6 fw-bold text-gray-700 me-5">
                            Duration: {fair.durationHours}h {fair.durationMinutes}m
                          </span>
                        </div>

                        <div className='row g-0 mb-6'>
                          <div className='col-6 bg-light rounded-start p-4 border-end border-white'>
                            <div className='d-flex align-items-center'>
                              <Calendar3 className='text-primary fs-2 me-3' size={24} />
                              <div>
                                <div className='fs-8 fw-bold text-gray-500'>Start Date</div>
                                <div className='fs-7 fw-bolder text-gray-800'>{fair.startDate}</div>
                              </div>
                            </div>
                          </div>
                          <div className='col-6 bg-light rounded-end p-4'>
                            <div className='d-flex align-items-center'>
                              <Clock className='text-primary fs-2 me-3' size={24} />
                              <div>
                                <div className='fs-8 fw-bold text-gray-500'>Start Time</div>
                                <div className='fs-7 fw-bolder text-gray-800'>{fair.startTime}  <span className="badge badge-light-primary fw-bold px-3 py-2">
                                  {fair.timezone || 'UTC'}
                                </span></div>
                                {/* Timezone Badge */}


                              </div>
                            </div>
                          </div>
                        </div>

                        <div className='mb-6'>
                          {fair.isActivated ?
                            <div className='w-100'>
                              <label className='form-label fw-bold text-gray-700 fs-7'>Job Seeker Link</label>
                              <div className='input-group input-group-solid'>
                                <input
                                  title={`${config.vjfpBaseUrl}${fair.alias ? `join/${fair.alias}` : `job-seeker/organizations/${fair.companyId}/jobfairs/${fair.id}`}`}
                                  type='text'
                                  className='form-control form-control-sm form-control-solid'
                                  value={`${config.vjfpBaseUrl}${fair.alias ? `join/${fair.alias}` : `job-seeker/organizations/${fair.companyId}/jobfairs/${fair.id}`}`}
                                  readOnly />
                                <span
                                  className='input-group-text cursor-pointer hover-primary'
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${config.vjfpBaseUrl}${fair.alias ? `join/${fair.alias}` : `job-seeker/organizations/${fair.companyId}/jobfairs/${fair.id}`}`);
                                    const label = document.getElementById(`copy-label-card-btn-${fair.id}`);
                                    label!.classList.remove('d-none');
                                    setTimeout(() => label!.classList.add('d-none'), 2000);
                                  }}
                                >
                                  <Clipboard size={18} />
                                </span>
                              </div>
                              <div className="d-flex justify-content-end mt-1" style={{ minHeight: '20px' }}>
                                <span id={`copy-label-card-btn-${fair.id}`} className='text-success fw-bold fs-8 d-none'>Copied!</span>
                              </div>
                            </div>
                            :
                            <div className="alert alert-warning d-flex align-items-center p-5 mb-0">
                              <ExclamationTriangle className="svg-icon svg-icon-2hx svg-icon-warning me-4" size={24} />
                              <div className="d-flex flex-column">
                                <h5 className="mb-1 text-warning">Pending Activation</h5>
                                <span>Wait for admin approval. You'll be notified soon.</span>
                              </div>
                            </div>
                          }
                        </div>
                        {fair.paymentStatus === 'UnPaid' && billingConfigLoaded && jobfairPaymentsEnabled && (
                          <div className='d-flex flex-column gap-2'>
                            <input
                              type="text"
                              className="form-control form-control-solid"
                              placeholder="Have a promo code?"
                              value={promoCodes[fair.id] || ''}
                              onChange={(e) => setPromoCodes({...promoCodes, [fair.id]: e.target.value})}
                            />
                            <div className='d-grid'>
                              <Button
                                variant='success'
                                onClick={() => handlePayment(fair)}
                                disabled={isPaymentLoading === fair.id}
                              >
                                {isPaymentLoading === fair.id ? 'Processing...' : 'Pay Now'}
                              </Button>

                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              }</>
            ) : (
              <div className='py-10'>
                <div>
                  <div className="mb-4">
                    <Calendar3 size={48} className="text-gray-300" />
                  </div>
                  {jobFairs.length > 0 ? (
                    <>
                      <h5 className="text-gray-600">All Job Fairs are in the past</h5>
                      <p className="text-gray-500 mb-5">Toggle "Show Past" to view them.</p>
                    </>
                  ) : (
                    <>
                      <h5 className="text-gray-600">No Job Fairs Scheduled</h5>
                      <p className="text-gray-500 mb-5">Create your first job fair to get started.</p>
                      <CreateEditJobFair orgName={props?.orgName || ''} onSubmit={handleCreateJobFair} jobfairPaymentsEnabled={jobfairPaymentsEnabled} billingConfigLoaded={billingConfigLoaded}></CreateEditJobFair>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>)}
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedJobFair ? 'Edit Job Fair' : 'Create New Job Fair'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateEditJobFair orgName={props?.orgName || ''} onSubmit={handleCreateJobFair} jobFair={selectedJobFair} jobfairPaymentsEnabled={jobfairPaymentsEnabled} billingConfigLoaded={billingConfigLoaded} ></CreateEditJobFair>
        </Modal.Body>
      </Modal>
      <AppModal
        show={showDeleteAlert}
        onSuccess={confirmDeleteJobFair}
        onReject={() => setShowDeleteAlert(false)}
        onHide={() => setShowDeleteAlert(false)}
        title="Delete this Job Fair?"
      > Are you sure to delete this job fair event
      </AppModal>
      <AlertModal
        show={showErrorModal}
        onOK={() => setShowErrorModal(false)}
        title="Payment Error"
      >
        {errorMessage}
      </AlertModal>
    </div >
  );
};

export default ManageJobFairs;
